import { useState, useEffect, useRef } from "react"
import { Link } from "react-router-dom"
import { getClients } from "../../api/clients"
import { getRoutines } from "../../api/routines"
import type { Client, Routine, RoutineExercise, RepsUnit, WeightUnit } from "../../types"

// Hora actual en Costa Rica (UTC-6)
function getCRTime(): Date {
  const now = new Date()
  // Costa Rica es UTC-6 fijo (no cambia por horario de verano)
  const utc = now.getTime() + now.getTimezoneOffset() * 60000
  return new Date(utc - 6 * 3600000)
}

function useCRClock() {
  const [time, setTime] = useState(getCRTime())
  const ref = useRef<ReturnType<typeof setInterval> | null>(null)
  useEffect(() => {
    ref.current = setInterval(() => setTime(getCRTime()), 1000)
    return () => { if (ref.current) clearInterval(ref.current) }
  }, [])
  return time
}

function formatClock(d: Date) {
  const h = String(d.getHours()).padStart(2, "0")
  const m = String(d.getMinutes()).padStart(2, "0")
  const s = String(d.getSeconds()).padStart(2, "0")
  return { h, m, s, hour: d.getHours() }
}

// ─── Colores por grupo muscular ───────────────────────────────────────────────
const MUSCLE_COLORS: Record<string, string> = {
  legs:      "bg-green-500/20 text-green-400 border border-green-500/30",
  chest:     "bg-red-500/20 text-red-400 border border-red-500/30",
  back:      "bg-blue-500/20 text-blue-400 border border-blue-500/30",
  shoulders: "bg-purple-500/20 text-purple-400 border border-purple-500/30",
  arms:      "bg-orange-500/20 text-orange-400 border border-orange-500/30",
  core:      "bg-yellow-500/20 text-yellow-400 border border-yellow-500/30",
  "back/legs": "bg-teal-500/20 text-teal-400 border border-teal-500/30",
}
function getMuscleColor(group: string) {
  return MUSCLE_COLORS[group?.toLowerCase()] ?? "bg-white/10 text-white/50 border border-white/10"
}

// ─── Helpers de fecha ─────────────────────────────────────────────────────────
const DAY_NAMES   = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"]
const MONTH_NAMES = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
]

function toISODate(d: Date) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

function getWeekDays(base: Date) {
  const start = new Date(base)
  // getDay(): 0=Dom, 1=Lun … 6=Sáb. Tratamos Dom como 7 para que quede al final.
  const dow = start.getDay() === 0 ? 7 : start.getDay()
  start.setDate(start.getDate() - dow + 1) // retroceder al lunes
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start)
    d.setDate(start.getDate() + i)
    return d
  })
}

// ─── Tipos internos ───────────────────────────────────────────────────────────
interface ClientSchedule {
  client: Client
  routines: Routine[]
}

type FilterType = "all" | "group_class" | "personal"

const GYM_HOURS = Array.from({ length: 15 }, (_, i) => i + 6) // 6–20

// ─── Página principal ─────────────────────────────────────────────────────────
export default function TrainingDisplayPage() {
  const [selectedDate, setSelectedDate] = useState(new Date())
  const [schedules, setSchedules]       = useState<ClientSchedule[]>([])
  const [loading, setLoading]           = useState(true)
  const [filter, setFilter]             = useState<FilterType>("all")

  const crTime = useCRClock()
  const { h, m, s, hour: currentHour } = formatClock(crTime)

  // Hora seleccionada en el dropdown (null = todo el día)
  const [selectedHour, setSelectedHour] = useState<number | null>(
    GYM_HOURS.includes(currentHour) ? currentHour : null
  )

  // Auto-avanza el dropdown cuando el reloj pasa a la siguiente hora,
  // pero solo si el usuario no ha seleccionado manualmente una hora diferente.
  const prevHourRef = useRef(currentHour)
  useEffect(() => {
    if (currentHour !== prevHourRef.current) {
      if (selectedHour === prevHourRef.current) {
        // El usuario estaba en la hora automática → avanzar
        setSelectedHour(GYM_HOURS.includes(currentHour) ? currentHour : null)
      }
      prevHourRef.current = currentHour
    }
  }, [currentHour, selectedHour])

  // Filtro estricto: si se selecciona una hora, solo muestra rutinas cuyo bloque
  // la incluye. Rutinas sin horario asignado quedan ocultas hasta que se elija "Todo el día".
  function filterRoutinesByHour(routines: Routine[]): Routine[] {
    if (selectedHour === null) return routines
    return routines.filter(
      (r) => r.start_hour != null && r.end_hour != null &&
             selectedHour >= r.start_hour && selectedHour < r.end_hour
    )
  }

  const days = getWeekDays(selectedDate)

  useEffect(() => {
    const dateStr = toISODate(selectedDate)
    setLoading(true)
    getClients()
      .then((clients) =>
        Promise.all(
          clients.map((client) =>
            getRoutines({ userId: client.id, date: dateStr }).then((routines) => ({ client, routines }))
          )
        )
      )
      .then((results) => setSchedules(results.filter((s) => s.routines.length > 0)))
      .finally(() => setLoading(false))
  }, [selectedDate])

  const filtered = schedules
    .map((s) => ({ ...s, routines: filterRoutinesByHour(s.routines) }))
    .filter((s) => s.routines.length > 0)
    .filter((s) => {
      if (filter === "group_class") return s.client.client_type === "group_class"
      if (filter === "personal")    return s.client.client_type === "personal"
      return true
    })

  const monthYear = `${MONTH_NAMES[selectedDate.getMonth()]} ${selectedDate.getFullYear()}`

  const navigate = (delta: number) => {
    const d = new Date(selectedDate)
    d.setDate(d.getDate() + delta)
    setSelectedDate(d)
  }

  return (
    <div className="min-h-screen px-6 py-4">

      {/* ── Header ── */}
      <div className="mb-5 flex items-center justify-between">
        <Link
          to="/dashboard"
          className="flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-2 text-xs text-muted hover:text-white transition"
        >
          <span>←</span>
          <span>Dashboard</span>
        </Link>
        <h1 className="text-xl font-bold tracking-[0.25em] text-white">TRAINING PLATFORM</h1>

        {/* Reloj + dropdown de hora */}
        <div className="flex items-center gap-3">
          {/* Reloj CR */}
          <div className="flex items-center gap-1.5 rounded-xl border border-white/10 bg-white/5 px-4 py-2">
            <span className="text-[10px] font-medium text-muted tracking-widest">CR</span>
            <span className="font-mono text-lg font-bold text-primary tabular-nums">{h}:{m}</span>
            <span className="font-mono text-sm text-muted tabular-nums">{s}</span>
          </div>

          {/* Dropdown de hora */}
          <HourDropdown value={selectedHour} onChange={setSelectedHour} />
        </div>
      </div>

      {/* ── Controls bar ── */}
      <div className="mb-6 flex flex-wrap items-center gap-3 justify-between">

        {/* Semana */}
        <div className="flex items-center gap-1">
          <NavBtn onClick={() => navigate(-7)}>‹</NavBtn>

          {/* Mes/Año */}
          <div className="flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-2 text-xs text-muted">
            <CalendarIcon />
            <span>{monthYear}</span>
          </div>

          {/* Días */}
          {days.map((day) => {
            const active = toISODate(day) === toISODate(selectedDate)
            return (
              <button
                key={toISODate(day)}
                onClick={() => setSelectedDate(day)}
                className={`flex flex-col items-center rounded-lg px-2.5 py-1.5 text-xs transition ${
                  active ? "bg-primary text-black font-bold" : "text-muted hover:text-white"
                }`}
              >
                <span className="text-[10px] tracking-widest">{DAY_NAMES[day.getDay()]}</span>
                <span className="text-sm font-bold leading-none mt-0.5">
                  {String(day.getDate()).padStart(2, "0")}
                </span>
              </button>
            )
          })}

          <NavBtn onClick={() => navigate(7)}>›</NavBtn>
        </div>

        {/* Filtro tipo cliente */}
        <div className="flex overflow-hidden rounded-lg border border-white/10 text-sm">
          {(["group_class", "personal"] as const).map((type, i) => (
            <button
              key={type}
              onClick={() => setFilter(filter === type ? "all" : type)}
              className={`px-4 py-2 transition ${i > 0 ? "border-l border-white/10" : ""} ${
                filter === type
                  ? "bg-primary text-black font-semibold"
                  : "text-muted hover:text-white"
              }`}
            >
              {type === "group_class" ? "Group Class" : "Personal"}
            </button>
          ))}
        </div>
      </div>

      {/* ── Content ── */}
      {loading ? (
        <div className="text-muted text-sm">Cargando rutinas…</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-white/10 bg-surface p-10 text-center text-muted">
          {selectedHour !== null ? (
            <>
              <p>No hay rutinas para el bloque {String(selectedHour).padStart(2,"0")}:00 – {String(selectedHour+1).padStart(2,"0")}:00.</p>
              <p className="mt-2 text-xs">
                Asigná el bloque horario en Dashboard → Training → Manage Training Program,<br />
                o seleccioná "Todo el día" para ver todas.
              </p>
            </>
          ) : (
            <>
              <p>No hay rutinas programadas para este día.</p>
              <p className="mt-2 text-xs">
                Asigná rutinas en Dashboard → Training → Manage Training Program.
              </p>
            </>
          )}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filtered.map(({ client, routines }) => (
            <ClientCard
              key={client.id}
              client={client}
              routines={routines}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Dropdown de hora (custom, dark-theme) ────────────────────────────────────
const ALL_OPTIONS = [null, ...GYM_HOURS] // null = "Todo el día"

function HourDropdown({
  value,
  onChange,
}: {
  value: number | null
  onChange: (v: number | null) => void
}) {
  const [open, setOpen] = useState(false)
  const wrapRef  = useRef<HTMLDivElement>(null)
  const listRef  = useRef<HTMLDivElement>(null)
  const activeRef = useRef<HTMLButtonElement>(null)

  // Cierra al hacer clic fuera
  useEffect(() => {
    function handleOutside(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener("mousedown", handleOutside)
    return () => document.removeEventListener("mousedown", handleOutside)
  }, [])

  // Scroll al elemento activo al abrir
  useEffect(() => {
    if (open && activeRef.current && listRef.current) {
      activeRef.current.scrollIntoView({ block: "nearest" })
    }
  }, [open])

  const label =
    value === null
      ? "Todo el día"
      : `${String(value).padStart(2, "0")}:00 – ${String(value + 1).padStart(2, "0")}:00`

  return (
    <div ref={wrapRef} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex min-w-[160px] items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-sm text-white hover:border-white/20 transition"
      >
        <span>{label}</span>
        <span className="text-muted text-xs leading-none">{open ? "▴" : "▾"}</span>
      </button>

      {open && (
        <div
          ref={listRef}
          className="absolute right-0 top-full z-50 mt-1 max-h-72 w-48 overflow-y-auto rounded-xl border border-white/10 bg-[#161616] shadow-2xl"
        >
          {ALL_OPTIONS.map((opt) => {
            const isSelected = opt === value
            const optLabel =
              opt === null
                ? "Todo el día"
                : `${String(opt).padStart(2, "0")}:00 – ${String(opt + 1).padStart(2, "0")}:00`
            return (
              <button
                key={opt ?? "all"}
                ref={isSelected ? activeRef : undefined}
                onClick={() => { onChange(opt); setOpen(false) }}
                className={`w-full px-4 py-2 text-left text-sm transition ${
                  isSelected
                    ? "bg-primary/15 text-primary font-semibold"
                    : opt === null
                    ? "text-white/50 hover:bg-white/5 hover:text-white"
                    : "text-white/70 hover:bg-white/5 hover:text-white"
                }`}
              >
                {optLabel}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ─── Tarjeta por cliente ──────────────────────────────────────────────────────
function ClientCard({ client, routines }: { client: Client; routines: Routine[] }) {
  const exercises = routines.flatMap((r) => r.routine_exercises)
  const initials  = client.name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()
  const block     = routines[0]

  return (
    <div className="rounded-xl overflow-hidden border border-white/10 bg-surface">
      {/* Header */}
      <div className="flex items-center gap-3 bg-surface-light px-4 py-3">
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary/20 text-sm font-bold text-primary">
          {initials}
        </div>
        <div>
          <p className="text-sm font-semibold text-white">{client.name}</p>
          <p className="text-xs text-muted">{exercises.length} ejercicio{exercises.length !== 1 ? "s" : ""}</p>
          {block?.start_hour != null && (
            <p className="text-[10px] font-mono text-primary/60">
              {String(block.start_hour).padStart(2,"0")}:00 – {String(block.end_hour).padStart(2,"0")}:00
            </p>
          )}
        </div>
      </div>

      {/* Ejercicios */}
      <div className="divide-y divide-white/5 max-h-[420px] overflow-y-auto rounded-b-xl">
        {exercises.map((re) => (
          <ExerciseRow key={re.id} re={re} />
        ))}
      </div>
    </div>
  )
}

// ─── Formateo de métricas ─────────────────────────────────────────────────────
function formatReps(sets: number, reps: number, unit: RepsUnit): string {
  switch (unit) {
    case "reps_per_side": return `${sets}×${reps}/lado`
    case "rir":           return `${sets}×${reps} RIR`
    case "rpe":           return `${sets}×${reps} RPE`
    default:              return `${sets}×${reps}`
  }
}

function formatWeight(weight: number, unit: WeightUnit): string {
  switch (unit) {
    case "lb":         return `${weight} lb`
    case "rm_percent": return `${weight}% RM`
    case "bw_percent": return `${weight}% BW`
    default:           return `${weight} kg`
  }
}

// ─── Fila de ejercicio ────────────────────────────────────────────────────────
function ExerciseRow({ re }: { re: RoutineExercise }) {
  const { exercise } = re
  return (
    <div className="flex gap-3.5 p-3.5">

      {/* GIF / thumbnail */}
      <div className="h-[116px] w-[116px] flex-shrink-0 overflow-hidden rounded-lg border border-white/10 bg-black/40">
        {exercise.gif_url ? (
          <img
            src={exercise.gif_url}
            alt={exercise.name}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-5xl text-white/15">💪</div>
        )}
      </div>

      {/* Datos */}
      <div className="flex-1 min-w-0">
        {/* Nombre + badge */}
        <div className="flex items-start justify-between gap-1 mb-2">
          <p className="text-sm font-semibold text-white leading-tight truncate">{exercise.name}</p>
          <span className={`flex-shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-medium leading-tight ${getMuscleColor(exercise.muscle_group)}`}>
            {exercise.muscle_group}
          </span>
        </div>

        {/* Métricas */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
          <Metric icon={<BarbellIcon />} value={formatReps(re.sets, re.reps, re.reps_unit)} />
          {re.tempo  && <Metric icon={<TimerIcon />}  value={re.tempo} />}
          {re.weight != null && <Metric icon={<WeightIcon />} value={formatWeight(re.weight, re.weight_unit)} />}
        </div>

        {/* Notas */}
        {re.notes && (
          <p className="mt-1.5 flex items-start gap-1 text-[11px] leading-tight text-white/40">
            <NoteIcon />
            <span>{re.notes}</span>
          </p>
        )}
      </div>
    </div>
  )
}

// ─── Sub-componentes ──────────────────────────────────────────────────────────
function Metric({ icon, value }: { icon: React.ReactNode; value: string }) {
  return (
    <span className="flex items-center gap-1 text-white/70">
      {icon}
      <span>{value}</span>
    </span>
  )
}

function NavBtn({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className="rounded-lg border border-white/10 px-2.5 py-2 text-base text-muted hover:text-white transition leading-none"
    >
      {children}
    </button>
  )
}

// ─── Iconos SVG ───────────────────────────────────────────────────────────────
function CalendarIcon() {
  return (
    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  )
}

function PersonIcon() {
  return (
    <svg className="h-4 w-4 text-white/20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  )
}

function BarbellIcon() {
  return (
    <svg className="h-3 w-3 flex-shrink-0 text-primary/60" viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.57 14.86L22 13.43 20.57 12 17 15.57 8.43 7 12 3.43 10.57 2 9.14 3.43 7.71 2 5.57 4.14 4.14 2.71 2.71 4.14l1.43 1.43L2 7.71l1.43 1.43L2 10.57 3.43 12 7 8.43 15.57 17 12 20.57 13.43 22l1.43-1.43L16.29 22l2.14-2.14 1.43 1.43 1.43-1.43-1.43-1.43L22 16.29l-1.43-1.43z"/>
    </svg>
  )
}

function TimerIcon() {
  return (
    <svg className="h-3 w-3 flex-shrink-0 text-primary/60" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <circle cx="12" cy="13" r="8"/>
      <path strokeLinecap="round" d="M12 9v4l2 2M9 3h6M12 3v2"/>
    </svg>
  )
}

function WeightIcon() {
  return (
    <svg className="h-3 w-3 flex-shrink-0 text-primary/60" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" d="M6 4v16M18 4v16M2 9h4M18 9h4M2 15h4M18 15h4M6 12h12"/>
    </svg>
  )
}

function NoteIcon() {
  return (
    <svg className="h-3 w-3 mt-0.5 flex-shrink-0 text-white/30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
    </svg>
  )
}
